import React, { useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Detective, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PlagiarismCheckModal({ open, onOpenChange, examId, questionId }) {
  const [busy, setBusy] = useState(false);
  const [pairs, setPairs] = useState(null);
  const [analyzed, setAnalyzed] = useState(0);
  const [provider, setProvider] = useState("claude");
  const [threshold, setThreshold] = useState(0.7);

  const run = async () => {
    setBusy(true);
    setPairs(null);
    try {
      const r = await api.post("/ai/plagiarism-check", {
        exam_id: examId,
        question_id: questionId,
        provider,
        threshold,
      });
      setPairs(r.data.pairs || []);
      setAnalyzed(r.data.submissions_analyzed || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plagiarism check failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="plagiarism-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Detective size={22} weight="fill" /> AI Plagiarism Detection
          </DialogTitle>
          <DialogDescription>
            Compares all submitted short-answer responses for this question using {provider === "claude" ? "Claude Sonnet 4.5" : "GPT-5.2"}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Provider</div>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white"
              data-testid="plag-provider"
            >
              <option value="claude">Claude 4.5</option>
              <option value="openai">GPT-5.2</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Threshold</div>
            <input
              type="number" step="0.05" min="0" max="1"
              value={threshold}
              onChange={(e) => setThreshold(parseFloat(e.target.value) || 0.7)}
              className="w-full border border-zinc-300 px-3 py-2 text-sm"
              data-testid="plag-threshold"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={run}
              disabled={busy}
              className="w-full bg-zinc-900 text-white py-2 text-sm font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="plag-run-btn"
            >
              {busy ? "Analyzing…" : "Run check"}
            </button>
          </div>
        </div>

        {pairs !== null && (
          <div data-testid="plag-results">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3 font-semibold">
              {analyzed} submissions analyzed · {pairs.length} suspicious pair{pairs.length === 1 ? "" : "s"}
            </div>
            {pairs.length === 0 ? (
              <div className="border border-green-200 bg-green-50 p-6 text-sm text-green-800">
                No suspicious similarity above threshold. ✓
              </div>
            ) : (
              <div className="border border-zinc-200">
                {pairs.map((p, i) => (
                  <div key={i} className="p-4 border-b border-zinc-200 last:border-b-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Warning size={16} weight="fill" className="text-amber-600" />
                        <span className="font-mono-tab text-lg font-bold">{Math.round(p.similarity * 100)}%</span>
                        <span className="text-xs text-zinc-500">similarity</span>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="border border-zinc-200 p-3 bg-zinc-50">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">{p.a.name}</div>
                        <div className="text-sm font-mono whitespace-pre-wrap">{p.a.text}</div>
                      </div>
                      <div className="border border-zinc-200 p-3 bg-zinc-50">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-semibold">{p.b.name}</div>
                        <div className="text-sm font-mono whitespace-pre-wrap">{p.b.text}</div>
                      </div>
                    </div>
                    {p.reason && (
                      <div className="mt-2 text-xs text-zinc-600 italic">{p.reason}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
